import {
  Building2,
  Layers,
  CalendarDays,
  CalendarCheck,
  CalendarOff,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  ClipboardX,
  HardHat,
  Hammer,
  LayoutDashboard,
  Menu,
  Package,
  PackageCheck,
  PanelLeftClose,
  PanelLeftOpen,
  Ruler,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  UserCircle2,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  pageForPath,
  tabForPath,
  tabsOpenedBy,
  type PageDefinition,
} from '@obraplan/core/domain/access/pages';
import type { PermissionKey } from '@obraplan/core/domain/access/permissions';
import { BrandMark, BrandWordmark } from '@/components/brand/BrandMark';
import { EmptyState } from '@/components/feedback/states';
import { usePermissionsAnywhere, useVisiblePages } from '@/features/session/use-session';
import { ThemeToggle } from '@/features/theme/ThemeToggle';
import { cn } from '@/lib/cn';
import { ProjectSelector } from '@/features/projects/ProjectSelector';
import { UserSwitcher } from '@/features/session/UserSwitcher';
import { ConnectionIndicator } from '@/features/offline/ConnectionIndicator';
import { usePreloadToday } from '@/features/offline/use-preload-today';
import { useOutboxSync } from '@/features/offline/use-outbox-sync';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /**
   * O caminho que acende o item, quando é mais largo que o `to`: o Cronograma leva à vista
   * por mão de obra (`/cronograma/itens`), mas fica aceso em qualquer uma das três vistas.
   */
  activeOn?: string;
}

/** O ícone de cada tela. O resto — rótulo, caminho, seção, quem abre — vem de `PAGE_CATALOG`. */
const PAGE_ICONS: Readonly<Record<string, typeof LayoutDashboard>> = {
  '/': LayoutDashboard,
  '/obras': Building2,
  '/estrutura': Layers,
  '/quantitativo': Ruler,
  '/cronograma': CalendarDays,
  '/suprimentos': PackageCheck,
  '/mao-de-obra': HardHat,
  '/planejamento': CalendarRange,
  '/programacao': CalendarCheck,
  '/conferencia': ClipboardCheck,
  '/qualidade': ClipboardList,
  '/nao-conformidades': ClipboardX,
  '/configuracoes/disciplinas': Layers,
  '/configuracoes/servicos': Hammer,
  '/configuracoes/checklists': ClipboardList,
  '/configuracoes/materiais': Package,
  '/configuracoes/perfis': Users,
  '/configuracoes/calendario': CalendarOff,
  '/configuracoes/armazenamento': Settings,
  '/configuracoes/integridade': ShieldCheck,
  '/configuracoes/auditoria': ScrollText,
};

/**
 * A entrada do menu de uma tela.
 *
 * Na tela de abas com rota (Quantitativo, Cronograma), o menu tem uma entrada só, que leva
 * à primeira aba que o perfil vê e fica acesa em todas (RN-629). A ordem é a do catálogo:
 * no Cronograma, a mão de obra primeiro (pedido de 19/10/2026).
 */
function toNavItem(page: PageDefinition, permissions: ReadonlySet<PermissionKey>): NavItem {
  const primeira = tabsOpenedBy(page, permissions).find((tab) => tab.path !== undefined);
  return {
    to: primeira?.path ?? page.path,
    label: page.label,
    icon: PAGE_ICONS[page.path] ?? LayoutDashboard,
    ...(primeira === undefined ? {} : { activeOn: page.path }),
  };
}

// Sem permissão: o próprio cadastro é a única coisa que todo perfil administra.
const MY_PROFILE: NavItem = { to: '/meu-perfil', label: 'Meu perfil', icon: UserCircle2 };

const SIDEBAR_STORAGE_KEY = 'obraplan.sidebar';
const NAV_ITEMS_ID = 'navegacao-principal-itens';

/**
 * O menu recolhido a ícones, lembrado no aparelho.
 *
 * Lembrado como o tema, e pela mesma razão: é do monitor, não da conta. Quem recolhe no
 * notebook de 13" quer o espaço de volta toda vez que abre; no monitor largo do escritório,
 * não. Só a forma recolhida é gravada — aberto é o padrão, e a ausência da chave já o diz.
 */
function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      if (collapsed) window.localStorage.setItem(SIDEBAR_STORAGE_KEY, 'collapsed');
      else window.localStorage.removeItem(SIDEBAR_STORAGE_KEY);
    } catch {
      // Sem gravar: a escolha vale até fechar a aba.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((atual) => !atual), []);
  return [collapsed, toggle];
}

/**
 * O nome ao lado do símbolo, no menu aberto. Só o logotipo: o subtítulo que havia aqui
 * ("Planejamento de obras") repetia o que a lista logo abaixo já diz.
 */
function BrandBlock() {
  return (
    <div className="flex min-w-0 flex-1 items-center">
      <BrandWordmark />
    </div>
  );
}

function NavSection({
  title,
  items,
  collapsed = false,
  onNavigate,
}: {
  title: string;
  items: readonly NavItem[];
  /** Menu recolhido a ícones: o título sai da tela, e fica só para o leitor de tela. */
  collapsed?: boolean;
  /** Fecha a gaveta no celular. No desktop não há gaveta e nada acontece. */
  onNavigate?: () => void;
}) {
  // Seção sem nenhum item visível não vira um título solto.
  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-0.5">
      <p
        className={cn(
          'px-3 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)',
          collapsed && 'sr-only',
        )}
      >
        {title}
      </p>
      {items.map((item) => (
        <NavEntry key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

/** O item está aceso? A raiz só na raiz; os outros, no caminho deles e nos subcaminhos. */
function isActiveOn(item: NavItem, pathname: string): boolean {
  const base = item.activeOn ?? item.to;
  if (base === '/') return pathname === '/';
  return pathname === base || pathname.startsWith(`${base}/`);
}

function NavEntry({
  item,
  collapsed = false,
  onNavigate,
}: {
  item: NavItem;
  collapsed?: boolean;
  onNavigate: (() => void) | undefined;
}) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  // Aceso à mão, e não pelo `NavLink`: o item do Cronograma aponta para uma vista e acende
  // nas três, e o `NavLink` só sabe acender no próprio destino.
  const isActive = isActiveOn(item, pathname);
  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate}
      // O rótulo quebra em duas linhas quando não cabe ("Cronograma por mão de obra"): num
      // menu, reticências escondem justamente a palavra que distingue um item do outro.
      // Recolhido, o rótulo sai da tela e vira a dica do ponteiro; para o leitor de tela
      // ele continua no link, só escondido (`sr-only`).
      title={collapsed ? item.label : undefined}
      className={cn(
        // No celular o item é alvo de dedo: 44px de altura, não 36.
        'relative flex min-h-11 items-center gap-2.5 rounded-md px-3 py-1.5 text-sm leading-snug transition-colors md:min-h-9',
        collapsed && 'size-10 min-h-0 justify-center px-0',
        isActive
          ? // O item aceso ganha o tom da marca e uma cota na borda do menu — o mesmo
            // marcador de nível do corte da Estrutura.
            'bg-(--color-brand-soft) font-medium text-(--color-content) before:absolute before:-left-3 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-r-full before:bg-(--color-brand) [&_svg]:text-(--color-brand)'
          : 'text-(--color-content-muted) hover:bg-(--color-surface-raised) hover:text-(--color-content)',
      )}
    >
      <Icon className={cn('shrink-0', collapsed ? 'size-5' : 'size-4')} aria-hidden="true" />
      <span className={cn(collapsed && 'sr-only')}>{item.label}</span>
    </Link>
  );
}

/**
 * A rota de uma tela que o papel não abre.
 *
 * O menu já não oferecia a porta, mas o endereço digitado, o favorito antigo e o link de
 * outra tela ainda chegam aqui. Esconder não é autorizar (RN-134) — a tela abriria e cada
 * leitura voltaria "sem permissão"; aqui ela nem abre, e diz por quê.
 *
 * A raiz é a exceção: é para onde a entrada no sistema leva. Quem não abre o Painel vai
 * para a primeira tela que abre, em vez de começar o dia numa recusa.
 */
function PageBlocked({
  page,
  fallback,
}: {
  page: PageDefinition;
  fallback: PageDefinition | undefined;
}) {
  const navigate = useNavigate();
  if (page.path === '/' && fallback !== undefined) return <Navigate to={fallback.path} replace />;

  return (
    <EmptyState
      title={page.path === '/' ? 'Nenhuma tela liberada' : `Sem acesso à tela ${page.label}`}
      description={
        page.path === '/'
          ? 'O seu papel não abre tela nenhuma. Quem administra os perfis pode liberá-las em Configurações → Perfis.'
          : 'O seu papel não abre esta tela. Quem administra os perfis pode liberá-la em Configurações → Perfis.'
      }
      icon={<ShieldAlert className="size-8" />}
      {...(fallback === undefined
        ? {}
        : {
            action: {
              label: `Ir para ${fallback.label}`,
              onClick: () => void navigate(fallback.path),
            },
          })}
    />
  );
}

export function AppShell() {
  const pages = useVisiblePages();
  const permissions = usePermissionsAnywhere();
  // A Conferência de hoje vai para o aparelho com sinal, para abrir sem ele (Fase 43, RN-600).
  usePreloadToday();
  // O que foi feito sem sinal sobe sozinho quando a conexão volta (Fase 43, RN-605).
  useOutboxSync();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const location = useLocation();

  const navItem = (page: PageDefinition) => toNavItem(page, permissions);
  const operation = pages.filter((page) => page.section === 'Operação').map(navItem);
  const settings = [
    MY_PROFILE,
    ...pages.filter((page) => page.section === 'Configurações').map(navItem),
  ];
  const current = pageForPath(location.pathname);
  const blocked =
    current !== undefined && !pages.some((page) => page.permission === current.permission);
  // A aba do endereço que o perfil não vê leva à primeira que ele vê (RN-629): o favorito
  // antigo de `/quantitativo/orcamento` não abre o orçamento para quem não o tem marcado.
  const abaDoEndereco = tabForPath(location.pathname);
  const abaPermitida =
    blocked || current === undefined
      ? undefined
      : tabsOpenedBy(current, permissions).find((tab) => tab.path !== undefined);
  const abaBloqueada =
    abaDoEndereco !== undefined && !permissions.has(abaDoEndereco.permission) && !blocked;

  // O título da aba é a tela: com várias abas abertas, "Cronograma · ObraPlan" diz qual
  // é qual, e "ObraPlan" sozinho não. A tela de entrada põe o dela (`LoginPage`).
  const titulo = current === undefined ? 'ObraPlan' : `${current.label} · ObraPlan`;
  useEffect(() => {
    document.title = titulo;
  }, [titulo]);

  // Trocar de tela fecha a gaveta. Sem isto, o menu ficaria por cima da tela que a
  // pessoa acabou de escolher — e o primeiro gesto no celular seria fechá-lo.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Enquanto a gaveta está aberta, quem rola é ela: a rolagem do conteúdo por baixo
  // faria a lista "escapar" do dedo.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  const collapseLabel = collapsed ? 'Expandir o menu' : 'Recolher o menu';

  return (
    <div className="flex min-h-full flex-col md:flex-row">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-(--color-surface-raised) focus:px-3 focus:py-2"
      >
        Pular para o conteúdo
      </a>

      {/*
        Duas navegações, uma por tamanho de tela — e não uma que se adapta.

        A faixa empilhada que servia aos dois ocupava **828px numa tela de 812px**: no
        celular, a primeira coisa que aparecia era o menu inteiro, e o conteúdo começava
        depois de uma rolagem. Aqui o desktop tem barra lateral fixa, e o celular tem uma
        gaveta que só existe quando é chamada.

        A barra do desktop recolhe a uma régua de ícones (64px) e lembra a escolha: numa
        tela de notebook, o Gantt e a tabela de horas valem mais do que os rótulos.

        A lista de itens é a mesma nas duas: quem decide o que aparece é o catálogo de
        telas (`PAGE_CATALOG`) cruzado com as permissões do perfil (RN-134).
      */}
      <nav
        aria-label="Navegação principal"
        data-state={collapsed ? 'collapsed' : 'expanded'}
        className={cn(
          'hidden shrink-0 flex-col border-r border-(--color-border-subtle) bg-(--color-surface-sunken) transition-[width] duration-200 ease-out motion-reduce:transition-none md:sticky md:top-0 md:flex md:h-dvh md:self-start',
          collapsed ? 'md:w-16' : 'md:w-60',
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2.5 px-3 pb-2 pt-3',
            collapsed && 'flex-col gap-2 px-0',
          )}
        >
          <BrandMark />
          {!collapsed && <BrandBlock />}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls={NAV_ITEMS_ID}
            aria-label={collapseLabel}
            title={collapseLabel}
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-(--color-content-muted) transition-colors hover:bg-(--color-surface-raised) hover:text-(--color-content)"
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="size-4" aria-hidden="true" />
            )}
          </button>
        </div>

        <div
          id={NAV_ITEMS_ID}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 [scrollbar-width:thin]"
        >
          <div
            className={cn(
              'flex flex-col',
              // Recolhido, o fio entre as seções faz o papel dos títulos que saíram.
              collapsed ? 'divide-y divide-(--color-border-subtle) [&>*]:py-2' : 'gap-5',
            )}
          >
            <NavSection title="Operação" items={operation} collapsed={collapsed} />
            <NavSection title="Configurações" items={settings} collapsed={collapsed} />
          </div>
        </div>

        <div
          className={cn(
            'border-t border-(--color-border-subtle) p-3',
            collapsed && 'flex justify-center px-0',
          )}
        >
          <ThemeToggle compact={collapsed} />
        </div>
      </nav>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Fechar o menu"
            className="gaveta-fundo absolute inset-0 bg-black/50"
            onClick={() => setMenuOpen(false)}
          />
          <nav
            aria-label="Navegação principal"
            className="gaveta absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-(--color-border-subtle) bg-(--color-surface-sunken)"
            onKeyDown={(event) => {
              if (event.key === 'Escape') setMenuOpen(false);
            }}
          >
            <div className="flex items-center gap-2.5 px-3 pb-2 pt-3">
              <BrandMark />
              <BrandBlock />
              <button
                type="button"
                aria-label="Fechar o menu"
                autoFocus
                className="-mr-1 flex size-11 shrink-0 items-center justify-center rounded-md text-(--color-content-muted) hover:bg-(--color-surface-raised)"
                onClick={() => setMenuOpen(false)}
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
              <div className="flex flex-col gap-5">
                <NavSection
                  title="Operação"
                  items={operation}
                  onNavigate={() => setMenuOpen(false)}
                />
                <NavSection
                  title="Configurações"
                  items={settings}
                  onNavigate={() => setMenuOpen(false)}
                />
              </div>
            </div>
            <div className="border-t border-(--color-border-subtle) p-3">
              <ThemeToggle />
            </div>
          </nav>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          O cabeçalho acompanha a rolagem no celular: sem ele fixo, trocar de obra ou
          abrir o menu exigiria voltar ao topo de uma folha do dia com trinta pessoas.
        */}
        <header className="sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b border-(--color-border-subtle) bg-(--color-surface) px-4 py-3 md:static md:gap-3">
          <button
            type="button"
            aria-label="Abrir o menu"
            aria-expanded={menuOpen}
            className="-ml-2 flex size-11 shrink-0 items-center justify-center rounded-md text-(--color-content-muted) hover:bg-(--color-surface-muted) md:hidden"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>

          {/*
            No celular são duas faixas: menu e perfil em cima, obra embaixo — e quem faz
            isso é o `w-full` do seletor sobre um `flex-wrap`, sem duplicar componente.

            Espremer os três numa linha só de 375px foi o que quebrou o cabeçalho: o
            seletor passava por baixo do perfil e o nome era cortado no meio.
          */}
          <div className="ml-auto flex min-w-0 items-center gap-1 md:order-last md:gap-3">
            <ConnectionIndicator />
            <UserSwitcher />
          </div>

          {/*
            Sem exceção por rota. Havia uma enquanto o quantitativo era rascunho do
            navegador e não pertencia a obra nenhuma; a fatia 1 da Fase 14 lhe deu obra, e
            com ela o seletor voltou a fazer sentido em toda tela. Caso especial em chrome
            compartilhado é dívida: ele sobrevive à razão que o criou.
          */}
          <div className="w-full min-w-0 md:order-first md:w-auto">
            <ProjectSelector />
          </div>
        </header>
        <main id="conteudo" className="min-w-0 flex-1 p-4 md:p-6">
          {blocked ? (
            <PageBlocked page={current} fallback={pages[0]} />
          ) : abaBloqueada && abaPermitida?.path !== undefined ? (
            <Navigate to={abaPermitida.path} replace />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
